import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Lightbulb as LightbulbIcon,
  Check as CheckIcon,
  Group as GroupIcon,
  MarkEmailRead as InvitedIcon
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import useSWR from 'swr';
import { useTheme } from '@mui/material/styles';
import SentInvitationsPanel from './SentInvitationsPanel';

function InviteToSuggestEntityDialog({ 
  open, 
  onClose, 
  entityType = 'consortium', 
  entity, 
  currentUserId, 
  mutate, 
  mutateUser,
  mode = 'invite',
  customUsers = null  // Optional: pass pre-filtered relevant users
}) {
  const theme = useTheme();
  const isSuggestMode = mode === 'suggest';
  
  // Color scheme based on mode
  const primaryColor = isSuggestMode ? '#ad89ad' : '#8064ffb8';
  const lightColor = isSuggestMode ? '#ad89ad36' : '#8a5cf622';
  const darkColor = isSuggestMode ? '#896b89' : '#59497f';
  const iconColor = isSuggestMode ? '#ad89ad' : '#59497fd4';
  

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [message, setMessage] = useState('');
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [alreadyInvitedUsers, setAlreadyInvitedUsers] = useState([]);
  const [checkingInvitations, setCheckingInvitations] = useState(false);
  const [alreadySuggestedUsers, setAlreadySuggestedUsers] = useState([]);
  const [checkingSuggestions, setCheckingSuggestions] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailInvitees, setEmailInvitees] = useState([]);
  const [emailError, setEmailError] = useState('');

  // Users for invite/suggest dialog
  // If customUsers are provided, use those; otherwise fetch all users
  const { data: users, error: usersError, isLoading: usersLoading } = useSWR(
    // Ensure fetcher runs only if `customUsers` are not provided and dialog is open
    (customUsers !== null && customUsers !== undefined) ? null : (open ? '/users/' : null),
    
    async (url) => {
      const res = await axiosService.get(url); // Perform the API call
      // console.log('[SWR] Fetched users raw response:', res.data); // Log raw API response
  
      // If `res.data.results` exists, prefer it; otherwise fall back to `res.data` or empty array
      return res?.data?.results || res?.data || [];
    },
    
    { revalidateOnFocus: false }
  );
  // useEffect(() => {
  //   console.log('[SWR State] users:', users?.results);
  //   console.log('[SWR State] usersLoading:', usersLoading);
  //   console.log('[SWR State] usersError:', usersError);
  // }, [users, usersLoading, usersError]);

  // Use customUsers if provided, otherwise use fetched users
  // Combine custom users and users from SWR
  const effectiveUsers = useMemo(() => {
    if (customUsers !== null && customUsers !== undefined) return customUsers;
    if (usersLoading || !users) return []; // Prevent filtering on empty datasets
    return users?.results || users || [];
  }, [customUsers, users, usersLoading]);

  // Debug logging
  // useEffect(() => {
  //   console.log('[InviteToSuggestDialog] Dialog state:', {
  //     open,
  //     mode,
  //     entityType,
  //     customUsers: customUsers?.length,
  //     users: users?.length,
  //     effectiveUsers: effectiveUsers?.length,
  //     usersLoading,
  //     entity: entity?.id
  //   });
  // }, [open, mode, entityType, customUsers, users, effectiveUsers, usersLoading, entity?.id]);



  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const consortiumMembers = entityType === 'consortium' && entity?.memberships 
    ? entity.memberships.map(m => m.member) 
    : [];



  // Check if user is already a member of the consortium
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isUserAlreadyMember = useMemo(() => {
    if (entityType !== 'consortium' || !consortiumMembers || !Array.isArray(consortiumMembers)) {
      return () => false;
    }
    
    // Create a Set of member IDs for faster lookup
    const memberIds = new Set(consortiumMembers.map(member => 
      String(member.user?.id || member.user_id || member.id || '')
    ));
    
    return (userId) => {
      return memberIds.has(String(userId));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, consortiumMembers]);

  // Check if user is already invited
  const isUserAlreadyInvited = (userId) => {
    if (!userId) return false;
    const userIdStr = String(userId);
    return alreadyInvitedUsers.includes(userIdStr);
  };


  // Fix the isUserAlreadySuggested function
  const isUserAlreadySuggested = (userId) => {
    if (!userId || !alreadySuggestedUsers || !Array.isArray(alreadySuggestedUsers)) {
      return false;
    }
    const userIdStr = String(userId);
    return alreadySuggestedUsers.includes(userIdStr);
  };
  

  // First, let's fix the fetchSuggestedUsers function to properly fetch suggestions
  const fetchSuggestedUsers = async () => {
    try {
      setCheckingSuggestions(true);
      // console.log('******* Fetching suggestions for entity:', entity.id, entityType);
      
      // Use the correct endpoint for suggestions by target
      const response = await axiosService.get(
        `/interact/suggestions/suggested-users-for-entity/${entityType}/${entity.id}/`
      );
      
      if (response.data && Array.isArray(response.data)) {
        // Extract recipient IDs from suggestions
        const suggestedUserIds = response.data.map(userId => String(userId));
        // console.log('Suggested user IDs from API:', suggestedUserIds);
        setAlreadySuggestedUsers(suggestedUserIds);
      } else {
        // console.log('No existing suggestions found');
        setAlreadySuggestedUsers([]);
      }
    } catch (error) {
      // If endpoint returns 404 or error, treat as no suggestions
      // console.log('No existing suggestions or API error:', error.message);
      setAlreadySuggestedUsers([]);
    } finally {
      setCheckingSuggestions(false);
    }
  };
  

  // Step-wise filtering: Filter out current user and already suggested users
  // Step-wise filtering: Remove current user, already suggested/invited users
  // Step-wise filtering: Remove current user, already suggested/invited users
  const availableUsers = useMemo(() => {
    // console.log('[AvailableUsers] Starting with effectiveUsers:', effectiveUsers?.length);
  
    if (!effectiveUsers || !Array.isArray(effectiveUsers) || usersLoading) {
      // console.log('[AvailableUsers] effectiveUsers is empty, not ready, or still loading.');
      return [];
    }
  
    // Step 1: Filter out current user
    const filtered = effectiveUsers.filter((candidate) => {
      const candidateId = String(candidate?.id || candidate?.pk || candidate?.public_id || '');
      return candidateId && candidateId !== String(currentUserId);
    });
  
    // console.log('[AvailableUsers] After filtering current user:', filtered.length);
  
    // Step 2: Filter out existing consortium members (both suggest and invite modes)
    const withoutMembers = entityType === 'consortium'
      ? filtered.filter((candidate) => {
          const candidateId = String(candidate?.id || candidate?.pk || candidate?.public_id || '');
          return !isUserAlreadyMember(candidateId);
        })
      : filtered;

    // Step 3: Filter out already-invited users (both modes)
    const withoutInvited = entityType === 'consortium'
      ? withoutMembers.filter((candidate) => {
          const candidateId = String(candidate?.id || candidate?.pk || candidate?.public_id || '');
          return !isUserAlreadyInvited(candidateId);
        })
      : withoutMembers;

    // Step 4: Additionally filter out already-suggested users in suggest mode
    if (isSuggestMode && alreadySuggestedUsers?.length > 0) {
      return withoutInvited.filter((candidate) => {
        const candidateId = String(candidate?.id || candidate?.pk || candidate?.public_id || '');
        return !alreadySuggestedUsers.includes(candidateId);
      });
    }

    return withoutInvited;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUsers, currentUserId, isSuggestMode, alreadySuggestedUsers, usersLoading, entityType, isUserAlreadyMember, isUserAlreadyInvited]);
  




  // Also update the useEffect to fetch both when dialog opens
  useEffect(() => {
    if (open && effectiveUsers && entity?.id) {
      if (entityType === 'consortium') {
        fetchInvitedUsers();
      }
      
      // Always fetch suggestions if in suggest mode
      if (isSuggestMode) {
        fetchSuggestedUsers();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, effectiveUsers, entity?.id, entityType, isSuggestMode]);
  
  // Update the user status display to show suggestion status
  const getUserStatus = (user) => {
    const userId = String(user?.id ?? user?.public_id ?? '');
    const statuses = [];
    
    if (entityType === 'consortium') {
      if (isUserAlreadyMember(userId)) {
        statuses.push({
          status: 'member',
          label: 'Already a member',
          icon: <GroupIcon fontSize="small" />,
          color: 'success',
          variant: 'outlined'
        });
      }
      
      if (isUserAlreadyInvited(userId)) {
        statuses.push({
          status: 'invited',
          label: 'Already invited',
          icon: <InvitedIcon fontSize="small" />,
          color: 'info',
          variant: 'outlined'
        });
      }
      
      // NEW: Show if already suggested (only in suggest mode)
      if (isSuggestMode && isUserAlreadySuggested(userId)) {
        statuses.push({
          status: 'suggested',
          label: 'Already suggested this entity',
          icon: <LightbulbIcon fontSize="small" />,
          color: 'warning',
          variant: 'outlined'
        });
      }
      
      if (consortiumCallId && isUserFromSameCall(user)) {
        statuses.push({
          status: 'same_call',
          label: 'From same funding call',
          icon: <BusinessIcon fontSize="small" />,
          color: 'info',
          variant: 'outlined'
        });
      }
    }
    
    return statuses;
  };
  
  // Update the handleUserToggle function to prevent selecting already suggested users in suggest mode
  const handleUserToggle = (candidate) => {
    const cid = String(candidate?.id ?? candidate?.pk ?? candidate?.public_id ?? '');
    
    // Validation checks based on mode
    if (entityType === 'consortium') {
      if (!isSuggestMode) {
        // INVITE MODE: Check if already member or invited
        if (isUserAlreadyInvited(cid)) {
          setError('This user has already been invited to this consortium.');
          return;
        }
        if (isUserAlreadyMember(cid)) {
          setError('This user is already a member of this consortium.');
          return;
        }
      } else {
        // SUGGEST MODE: Check if already suggested
        if (isUserAlreadySuggested(cid)) {
          setError('You have already suggested this consortium to this user.');
          return;
        }
        // Also check if already member (just show info, but allow suggesting)
        if (isUserAlreadyMember(cid)) {
          // Still allow suggesting, but show warning
          console.log('User is already a member, but allowing suggestion anyway');
        }
      }
    }
    
    const exists = selectedUsers.some((u) => String(u.id ?? u.pk ?? u.public_id ?? '') === cid);
    setSelectedUsers((prev) => 
      exists 
        ? prev.filter((u) => String(u.id ?? u.pk ?? u.public_id ?? '') !== cid) 
        : [...prev, candidate]
    );
    
    if (error) setError('');
  };
  

  const fetchInvitedUsers = async () => {
    try {
      setCheckingInvitations(true);
      // console.log('Bulk checking invitations for consortium:', entity.id);
      
      const response = await axiosService.get('/interact/consortium-invitations/invited-users-to-consortium/', {
        params: {
          consortium_id: entity.id,
        }
      });
      
      // Assuming response.data is an array of user IDs who are already invited
      if (Array.isArray(response?.data)) {
        const invitedUserIds = response.data.map(id => String(id));
        // console.log('Invited user IDs:', invitedUserIds);
        setAlreadyInvitedUsers(invitedUserIds);
      } else {
        console.error('Unexpected response format:', response?.data);
        setAlreadyInvitedUsers([]);
      }
      
    } catch (error) {
      console.error('Error in bulk invitation check:', error);
      setAlreadyInvitedUsers([]);
    } finally {
      setCheckingInvitations(false);
    }
  };


  // const isUserAlreadySuggested = (userId) => {

  //   if (!userId) return false;
  //   const userIdStr = String(userId);
  //   return alreadySuggestedUsers.includes(userIdStr);
  // };


  // Available users (filtered out already invited/members in invite mode)
  // const availableUsers = useMemo(() => {
  //   if (!users || !Array.isArray(users)) return [];
    
  //   return users.filter((candidate) => {
  //     const candidateId = String(candidate?.id ?? candidate?.pk ?? candidate?.public_id ?? '');
      
  //     // Skip current user
  //     if (!candidateId || candidateId === currentUserId) return false;
      
  //     // For consortium invite mode: Skip already invited or members
  //     if ( entityType === 'consortium') {
  //       if (isUserAlreadyInvited(candidateId) || isUserAlreadyMember(candidateId)) {
  //         return false;
  //       }
        
  //       if (isSuggestMode && isUserAlreadySuggested(candidateId)) {
  //         return false;
  //       }

  //     }
      
  //     return true;
  //   });
  // }, [users, currentUserId, alreadyInvitedUsers, isSuggestMode, entityType, isUserAlreadyMember]);





  const filteredUsers = useMemo(() => {
    if (!availableUsers || !Array.isArray(availableUsers)) return [];
  
    const q = searchTerm.toLowerCase().trim();
    if (!q) return availableUsers;
  
    return availableUsers.filter((candidate) => {
      const name = (candidate?.name || '').toLowerCase();
      const email = (candidate?.email || '').toLowerCase();
      const username = (candidate?.username || '').toLowerCase();
      const position = (candidate?.position || '').toLowerCase();
      const location = (candidate?.location || '').toLowerCase();
  
      return (
        name.includes(q) ||
        email.includes(q) ||
        username.includes(q) ||
        position.includes(q) ||
        location.includes(q)
      );
    });
  }, [availableUsers, searchTerm]);
  

  // Get consortium call ID if available
  const consortiumCallId = useMemo(() => {
    if (entityType === 'consortium' && entity?.funding_application?.call) {
      return entity.funding_application.call;
    }
    return null;
  }, [entityType, entity]);

  // Check if user is from the same call (for consortium entity type)
  const isUserFromSameCall = useMemo(() => {
    if (entityType !== 'consortium' || !consortiumCallId) {
      return () => false;
    }
    
    return (user) => {
      return user.consortiums?.some(consortium => 
        consortium?.funding_application?.call === consortiumCallId
      ) || false;
    };
  }, [entityType, consortiumCallId]);

  // Handle invite to entity
  const handleInvite = async () => {
    if (selectedUsers.length === 0 && emailInvitees.length === 0) {
      setError('Please select at least one user to invite.');
      return;
    }

    // Filter out users who are already members or already invited
    let finalSelectedUsers = [];
    let alreadyMembersInfo = [];
    let alreadyInvitedInfo = [];
    
    selectedUsers.forEach(user => {
      const userId = String(user?.id ?? user?.public_id ?? '');
      
      if (entityType === 'consortium') {
        if (isUserAlreadyMember(userId)) {
          alreadyMembersInfo.push(user.name || user.username || 'Unknown user');
        } else if (isUserAlreadyInvited(userId)) {
          alreadyInvitedInfo.push(user.name || user.username || 'Unknown user');
        } else {
          finalSelectedUsers.push(user);
        }
      } else {
        finalSelectedUsers.push(user);
      }
    });
    
    if (finalSelectedUsers.length === 0 && emailInvitees.length === 0) {
      let errorMessage = 'No valid users to invite.';
      if (alreadyMembersInfo.length > 0) {
        errorMessage += ` ${alreadyMembersInfo.length} user(s) are already members.`;
      }
      if (alreadyInvitedInfo.length > 0) {
        errorMessage += ` ${alreadyInvitedInfo.length} user(s) are already invited.`;
      }
      setError(errorMessage);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      const entityPublicId = entity?.id;
      if (!entityPublicId) {
        setError('Cannot invite: entity ID not available.');
        return;
      }

      const seen = new Set();
      const candidates = finalSelectedUsers.filter((c) => {
        const key = String(c?.id ?? c?.public_id ?? '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const results = [];
      for (const candidate of candidates) {
        const recipientPublicId = candidate?.id;
        if (!recipientPublicId) {
          results.push({ success: false, user: candidate, error: 'Missing user ID' });
          continue;
        }

        const payload = {
          [entityType]: entityPublicId,
          recipient: recipientPublicId,
        };

        try {
          const endpoint = getInviteEndpoint(entityType, entityPublicId);
          await axiosService.post(endpoint, payload);
          results.push({ success: true, user: candidate });
          
          // Update local state to mark as invited
          setAlreadyInvitedUsers(prev => [...prev, String(recipientPublicId)]);
        } catch (err) {
          const errorMsg = err?.response?.data?.detail || 
                          err?.response?.data?.message || 
                          'Failed to send invitation';
          results.push({ success: false, user: candidate, error: errorMsg });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      // Also send email-only invitations for unregistered addresses
      let emailSuccessCount = 0;
      if (!isSuggestMode && emailInvitees.length > 0) {
        emailSuccessCount = await sendEmailInvitations();
      }

      if (successCount + emailSuccessCount > 0) {
        setSelectedUsers([]);
        setEmailInvitees([]);
        setMessage('');
        mutate?.();
        mutateUser?.();
      }

      if (failCount > 0) {
        const failedNames = results
          .filter(r => !r.success)
          .slice(0, 3)
          .map(r => r.user?.name || r.user?.username || 'Unknown user')
          .join(', ');
        showSnackbar(`Invited ${successCount}. Failed for: ${failedNames}${failCount > 3 ? '…' : ''}`, 'warning');
      } else if (successCount + emailSuccessCount > 0) {
        const note = alreadyMembersInfo.length > 0 || alreadyInvitedInfo.length > 0
          ? ` (${alreadyMembersInfo.length + alreadyInvitedInfo.length} skipped)`
          : '';
        showSnackbar(`Successfully sent ${successCount + emailSuccessCount} invitation(s)${note}.`, 'success');
        onClose?.();
      }

    } catch (err) {
      console.error('Invitation error:', err);
      showSnackbar('Failed to send invitations. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle suggest entity to user
  // Handle suggest entity to user
  const handleSuggest = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user to suggest this entity to.');
      return;
    }
  
    // For consortium: Check if users are already members (just for info, still allow suggesting)
    let alreadyMembersInfo = [];
    if (entityType === 'consortium') {
      alreadyMembersInfo = selectedUsers
        .filter(user => {
          const userId = String(user?.id ?? user?.public_id ?? '');
          return isUserAlreadyMember(userId);
        })
        .map(user => user.name || user.username || 'Unknown user');
    }
  
    try {
      setLoading(true);
      setError('');
      setSuccess(false);
  
      const seen = new Set();
      const candidates = selectedUsers.filter((c) => {
        const key = String(c?.id ?? c?.public_id ?? '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  
      const results = [];
      const successfullySuggestedIds = []; // NEW: Track successfully suggested user IDs
      
      for (const candidate of candidates) {
        const recipientPublicId = candidate?.id;
        if (!recipientPublicId) {
          results.push({ success: false, user: candidate, error: 'Missing user ID' });
          continue;
        }
  
        const suggestionData = {
          recipient: recipientPublicId,
          body: message.trim()  || `I thought you might be interested in: ${entity.title || entity.name || entity.username || ''}`,
          kind: ['call', 'consortium'].includes(entityType) ? 'collaboration' : entityType
        };
  
        try {
          const endpoint = getSuggestEndpoint(entityType, entity.id);
          console.log('Suggesting to endpoint:', endpoint, 'with data:', suggestionData); 
  
          await axiosService.post(endpoint, suggestionData);
          results.push({ success: true, user: candidate });
          successfullySuggestedIds.push(String(recipientPublicId)); // NEW: Add to successful IDs
        } catch (err) {
          const errorMsg = err?.response?.data?.detail || 
                          err?.response?.data?.message || 
                          'Failed to send suggestion';
          results.push({ success: false, user: candidate, error: errorMsg });
        }
      }
  
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
  
      if (successCount > 0) {
        setAlreadySuggestedUsers(prev => Array.from(new Set([...prev, ...successfullySuggestedIds])));
        setSelectedUsers([]);
        setMessage('');
        mutate?.();
        mutateUser?.();
      }

      if (failCount > 0) {
        const failedNames = results
          .filter(r => !r.success)
          .slice(0, 3)
          .map(r => r.user?.name || r.user?.username || 'Unknown user')
          .join(', ');
        showSnackbar(`Suggested to ${successCount}. Failed for: ${failedNames}${failCount > 3 ? '…' : ''}`, 'warning');
      } else if (successCount > 0) {
        const note = alreadyMembersInfo.length > 0 ? ` (${alreadyMembersInfo.length} skipped)` : '';
        showSnackbar(`Successfully sent ${successCount} suggestion(s)${note}.`, 'success');
        onClose?.();
      }

    } catch (err) {
      console.error('Suggestion error:', err);
      showSnackbar('Failed to send suggestions. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Get appropriate endpoint based on entity type
  const getInviteEndpoint = (type, entityId) => {
    const endpoints = {
      consortium: `/interact/consortiums/${entityId}/invitations/`,
      // project: '/interact/project-invitations/',
      // call: '/interact/call-invitations/',
      // organization: '/interact/organization-invitations/',
    };
    return endpoints[type] || '/interact/invitations/';
  };

  const getSuggestEndpoint = (type, id) => {
    const endpoints = {
      consortium: `/interact/suggestions/by-target/consortium/${id}/`,
      project: `/interact/suggestions/by-target/project/${id}/`,
      call: `/interact/suggestions/by-target/call/${id}/`,
      organization: `/interact/suggestions/by-target/organization/${id}/`,
      user: `/interact/suggestions/by-target/user/${id}/`,
    };
    return endpoints[type] || '/interact/suggestions/';
  };

  // const handleUserToggle = (candidate) => {
  //   const cid = String(candidate?.id ?? candidate?.pk ?? candidate?.public_id ?? '');
    
  //   // Validation checks for consortium invite mode
  //   if (!isSuggestMode && entityType === 'consortium') {
  //     if (isUserAlreadyInvited(cid)) {
  //       setError('This user has already been invited to this consortium.');
  //       return;
  //     }
  //     if (isUserAlreadyMember(cid)) {
  //       setError('This user is already a member of this consortium.');
  //       return;
  //     }
  //   }
    
  //   const exists = selectedUsers.some((u) => String(u.id ?? u.pk ?? u.public_id ?? '') === cid);
  //   setSelectedUsers((prev) => 
  //     exists 
  //       ? prev.filter((u) => String(u.id ?? u.pk ?? u.public_id ?? '') !== cid) 
  //       : [...prev, candidate]
  //   );
    
  //   if (error) setError('');
  // };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedUsers([]);
    setMessage('');
    setError('');
    setSuccess(false);
    setAlreadyInvitedUsers([]);
    setEmailInput('');
    setEmailInvitees([]);
    setEmailError('');
    onClose();
  };

  // --- Email parsing helpers (support "Name <email>" and ";" separator) ---
  const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const _NAME_EMAIL_RE = /^([^<>]+?)\s*<([^<>]+)>\s*$/;

  const _parseEntry = (raw) => {
    raw = raw.trim();
    if (!raw) return null;
    const named = raw.match(_NAME_EMAIL_RE);
    if (named) {
      const name = named[1].trim();
      const email = named[2].trim().toLowerCase();
      return _EMAIL_RE.test(email) ? { name, email } : null;
    }
    const email = raw.toLowerCase();
    return _EMAIL_RE.test(email) ? { name: '', email } : null;
  };

  const _parseMultiple = (text) => {
    const valid = [], invalid = [];
    for (const part of text.split(/[;\n]+/)) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const entry = _parseEntry(trimmed);
      if (entry) valid.push(entry);
      else invalid.push(trimmed);
    }
    return { valid, invalid };
  };

  const addEmailInvitee = (text = emailInput) => {
    text = text.trim();
    if (!text) return;
    const { valid, invalid } = _parseMultiple(text);
    if (invalid.length && !valid.length) {
      setEmailError(
        invalid.length === 1
          ? `"${invalid[0]}" is not a valid email or "Name <email>" entry.`
          : `${invalid.length} entries could not be parsed. Use email or "Name <email@domain>" format.`
      );
      return;
    }
    const added = valid.filter(e => !emailInvitees.some(x => x.email === e.email));
    const dupes = valid.length - added.length;
    if (added.length) setEmailInvitees(prev => [...prev, ...added]);
    setEmailInput('');
    const parts = [];
    if (invalid.length) parts.push(`${invalid.length} invalid skipped`);
    if (dupes) parts.push(`${dupes} already added`);
    setEmailError(parts.length ? parts.join(', ') + '.' : '');
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmailError('');
    if (val.endsWith(';')) { addEmailInvitee(val); setEmailInput(''); }
    else setEmailInput(val);
  };

  const handleEmailPaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (pasted.includes(';') || pasted.includes('\n')) {
      e.preventDefault();
      const { valid, invalid } = _parseMultiple(pasted);
      const added = valid.filter(e => !emailInvitees.some(x => x.email === e.email));
      if (added.length) setEmailInvitees(prev => [...prev, ...added]);
      const parts = [];
      if (invalid.length) parts.push(`${invalid.length} invalid skipped`);
      const dupes = valid.length - added.length;
      if (dupes) parts.push(`${dupes} duplicate${dupes > 1 ? 's' : ''} skipped`);
      setEmailError(parts.length ? parts.join(', ') + '.' : '');
      setEmailInput('');
    }
  };

  const sendEmailInvitations = async () => {
    const results = await Promise.allSettled(
      emailInvitees.map(({ email, name }) =>
        axiosService.post('/interact/consortium-invitations/', {
          consortium: entity?.id,
          to_email: email,
          ...(name ? { to_name: name } : {}),
          message: message.trim() || undefined,
        })
      )
    );
    const failed = results.filter((r) => r.status !== 'fulfilled');
    if (failed.length > 0) {
      const data = failed[0].reason?.response?.data;
      const extract = (v) => (Array.isArray(v) ? v[0] : typeof v === 'string' ? v : null);
      const msg = extract(data?.to_email)
        || extract(data?.non_field_errors)
        || extract(data?.detail)
        || `${failed.length} email invitation(s) failed.`;
      setEmailError(msg);
    }
    return results.filter((r) => r.status === 'fulfilled').length;
  };

  const getEntityIcon = () => {
    switch(entityType) {
      case 'consortium': return <BusinessIcon sx={{ color: iconColor }} />;
      case 'call': return <EmailIcon sx={{ color: iconColor }} />;
      case 'project': return <LightbulbIcon sx={{ color: iconColor }} />;
      default: return <BusinessIcon sx={{ color: iconColor }} />;
    }
  };

  const getEntityTitle = () => {
    return entity?.name || entity?.title || `this ${entityType}`;
  };

  const getActionButtonText = () => {
    const action = isSuggestMode ? 'Suggest' : 'Invite';
    return selectedUsers.length === 0 
      ? `${action} Users` 
      : `${action} ${selectedUsers.length} User${selectedUsers.length > 1 ? 's' : ''}`;
  };

  const handleAction = () => {
    if (isSuggestMode) {
      handleSuggest();
    } else {
      handleInvite();
    }
  };

  const getDialogTitle = () => {
    const title = isSuggestMode ? `Suggest ${entityType}` : `Invite to ${entityType}`;
    const icon = isSuggestMode ? <SendIcon sx={{ color: iconColor }} /> : <PersonAddIcon sx={{ color: iconColor }} />;
    const entityName = getEntityTitle();
    const memberCount = entityType === 'consortium' && consortiumMembers ? consortiumMembers.length : null;

    return (
      <Box display="flex" alignItems="center" gap={1} flexWrap="nowrap" overflow="hidden">
        {icon}
        <Typography variant="h6" component="span" sx={{ whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
        <Chip
          label={entityName}
          size="small"
          variant="outlined"
          sx={{ color: darkColor, borderColor: iconColor, maxWidth: 200, flexShrink: 1 }}
        />
        {memberCount !== null && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            · {memberCount} member{memberCount !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
    );
  };

  const getInstructions = () => {
    const action = isSuggestMode ? 'suggest' : 'invite';
    let instruction = `Select colleagues to ${action} to "${getEntityTitle()}".`;
    
    if (entityType === 'consortium') {
      instruction += ' (Current members and already-invited users are not shown)';
    }
    
    return instruction;
  };

  const getMessagePlaceholder = () => {
    const action = isSuggestMode ? 'suggesting' : 'inviting';
    return `Add a personal message explaining why you're ${action} this ${entityType}...`;
  };

  const getMessageLabel = () => {
    return isSuggestMode ? "Suggestion Message (Optional)" : "Invitation Message (Optional)";
  };

  // Check if action button should be enabled
  const isActionEnabled = () => {
    if (loading || checkingInvitations) return false;

    // Email invitees alone are sufficient for invite mode
    if (!isSuggestMode && emailInvitees.length > 0) return true;

    // For consortium invitations: Only enable if at least one valid user is selected
    if (entityType === 'consortium' && !isSuggestMode) {
      return selectedUsers.some(user => {
        const userId = String(user?.id ?? user?.public_id ?? '');
        return !isUserAlreadyMember(userId) && !isUserAlreadyInvited(userId);
      });
    }

    return selectedUsers.length > 0;
  };

  // Get user status for display
  // const getUserStatus = (user) => {
  //   const userId = String(user?.id ?? user?.public_id ?? '');
  //   const statuses = [];
    
  //   if (entityType === 'consortium') {
  //     if (isUserAlreadyMember(userId)) {
  //       statuses.push({
  //         status: 'member',
  //         label: 'Already a member',
  //         icon: <GroupIcon fontSize="small" />,
  //         color: 'success',
  //         variant: 'outlined'
  //       });
  //     }
      
  //     if (isUserAlreadyInvited(userId)) {
  //       statuses.push({
  //         status: 'invited',
  //         label: 'Already invited',
  //         icon: <InvitedIcon fontSize="small" />,
  //         color: 'info',
  //         variant: 'outlined'
  //       });
  //     }
      
  //     if (consortiumCallId && isUserFromSameCall(user)) {
  //       statuses.push({
  //         status: 'same_call',
  //         label: 'From same funding call',
  //         icon: <BusinessIcon fontSize="small" />,
  //         color: 'info',
  //         variant: 'outlined'
  //       });
  //     }
  //   }
    
  //   return statuses;
  // };




  // Only show dialog when availableUsers are ready
  // If customUsers provided, no loading needed; otherwise wait for useSWR

  const isDialogReady = customUsers
        ? true
        : !usersLoading && effectiveUsers && effectiveUsers.length > 0;


  const shouldShowDialog = open && isDialogReady;

  // Show loading backdrop while preparing dialog
  if (open && !isDialogReady) {
    return (
      <Dialog open={open} maxWidth="sm">
        <Box display="flex" flexDirection="column" alignItems="center" p={4}>
          <CircularProgress size={60} sx={{ mb: 2, color: primaryColor }} />
          <Typography variant="h6" color="text.secondary">
            Loading users...
          </Typography>
        </Box>
      </Dialog>
    );
  }

  // if (open && !isDialogReady) {
  //   return (
  //     <Dialog 
  //       open={open} 
  //       maxWidth="sm"
  //       sx={{ zIndex: 3000 }}
  //       PaperProps={{
  //         sx: {
  //           bgcolor: 'background.paper',
  //           borderRadius: 2,
  //         }
  //       }}
  //     >
  //       <Box 
  //         display="flex" 
  //         flexDirection="column" 
  //         alignItems="center" 
  //         justifyContent="center" 
  //         p={4}
  //         sx={{
  //           minHeight: 200
  //         }}
  //       >
  //         <CircularProgress size={60} sx={{ mb: 2, color: primaryColor }} />
  //         <Typography variant="h6" color="text.secondary">
  //           Loading users...
  //         </Typography>
  //       </Box>
  //     </Dialog>
  //   );
  // }

  return (
    <Dialog 
      open={shouldShowDialog} 
      onClose={handleClose} 
      maxWidth="md" 
      sx={{  zIndex: 3000  }}
      fullWidth
      // PaperProps={{
        // sx: {
          // borderTop: `4px solid ${primaryColor}`,
        // }
      // }}
    >
      <DialogTitle >
        {getDialogTitle()}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, pb: 1, overflow: 'visible' }}>
        {/* Search field */}
        <TextField
          label="Search Users"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="Search by name, email, or position..."
          disabled={checkingInvitations}
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, color: 'action.active' }}>
                {((customUsers ? false : usersLoading) || checkingInvitations) ? <CircularProgress size={20} /> : null}
              </Box>
            ),
          }}
        />

        {/* User selection list */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: darkColor }}>
          Available Users ({selectedUsers.length} selected)
          {entityType === 'consortium' && consortiumMembers && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              ({consortiumMembers.length} current members, {alreadyInvitedUsers.length} already invited)
            </Typography>
          )}
        </Typography>

        <Paper sx={{ 
          maxHeight: 300, 
          overflow: 'auto', 
          border: `1px solid ${isSuggestMode ? '#ad89ad22' : 'divider'}`, 
          mb: 2,
          // bgcolor: isSuggestMode ? '#ad89ad22' : 'grey.50',
          opacity: checkingInvitations ? 0.7 : 1
        }}>



{/* Add checking suggestions indicator */}
      {(checkingInvitations || checkingSuggestions) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center">
            <CircularProgress size={16} sx={{ mr: 1 }} />
            {checkingInvitations ? 'Checking existing invitations...' : 
             checkingSuggestions ? 'Checking previous suggestions...' : 'Loading...'}
          </Box>
        </Alert>
      )}


          {usersError && !customUsers ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load users. Please close and try again.
            </Alert>
          ) : ((customUsers ? false : usersLoading) || checkingInvitations) ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? 'No users match your search.' : 'No users available.'}
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredUsers.map((candidate) => {
                const cid = String(candidate?.id);
                const isSelected = selectedUsers.some((u) => String(u.id) === cid);
                const userStatuses = getUserStatus(candidate);
                const isAlreadyMember = entityType === 'consortium' && isUserAlreadyMember(cid);
                const isAlreadyInvited = isUserAlreadyInvited(cid);
                const isDisabled = checkingInvitations || 
                  (entityType === 'consortium' && !isSuggestMode && (isAlreadyMember || isAlreadyInvited));
                
                return (
                  <ListItem 
                    key={cid}
                    secondaryAction={
                      <Checkbox 
                        edge="end" 
                        checked={isSelected}
                        disabled={isDisabled}
                        sx={{
                          color: iconColor,
                          '&.Mui-checked': {
                            color: iconColor,
                          },
                          '&.Mui-disabled': {
                            color: 'text.disabled',
                          },
                          mr: 2
                        }}
                      />
                    }
                    sx={{
                      bgcolor: isSelected ? lightColor : 'transparent',
                      '&:hover': { 
                        bgcolor: isDisabled ? 'transparent' : (isSuggestMode ? '#ad89ad22' : 'action.hover'),
                        cursor: isDisabled ? 'default' : 'pointer'
                      },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none'
                      },
                      opacity: isDisabled ? 0.6 : 1,
                  
                    }}
                    onClick={isDisabled ? undefined : () => handleUserToggle(candidate)}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={candidate.avatar}
                        // the app's avatar colour, as everywhere; an unavailable row is dimmed once, as a whole
                        // (the avatar faded again on top of it looked grey)
                        sx={{ 
                          bgcolor: 'primary.avatar',
                          color: 'white',
                          border: isSelected ? `2px solid ${iconColor}` : 'none',
                        }}
                      >
                        {(candidate.name || candidate.username || '?').charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="subtitle2">
                            {candidate.name || candidate.username}
                 
                          </Typography>
                          {candidate.position && (
                            <Chip 
                              label={candidate.position} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                borderColor: iconColor,
                                color: iconColor,
                                bgcolor: isSelected ? 'white' : 'transparent'
                              }}
                            />
                          )}
                          {userStatuses && userStatuses.map((status, index) => (
                            <Tooltip key={index} title={status.label}>
                              <Chip 
                                icon={status.icon}
                                label={status.status === 'member' ? 'Member' : 
                                       status.status === 'invited' ? 'Invited' : 
                                       'Same Call'}
                                size="small"
                                color={status.color}
                                variant={status.variant}
                                sx={{ ml: 0.5 }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {candidate.email || '—'}
                          </Typography>
                          {candidate.location && (
                            <Typography variant="caption" color="text.secondary">
                              📍 {candidate.location}
                            </Typography>
                          )}
                          {isAlreadyMember && !isSuggestMode && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                              Already a member of this consortium
                            </Typography>
                          )}
                          {isAlreadyInvited && !isSuggestMode && (
                            <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                              Already invited to this consortium
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>

        {/* Email-only invite section — invite mode only */}
        {!isSuggestMode && entityType === 'consortium' && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label="or invite by email"
              size="small"
              variant="outlined"
              sx={{ mb: 1, borderColor: primaryColor, color: darkColor }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Email or Name <email>"
                value={emailInput}
                error={Boolean(emailError)}
                helperText={emailError || 'Separate multiple with ;'}
                onChange={handleEmailChange}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmailInvitee(); } }}
                onPaste={handleEmailPaste}
                placeholder="alice@uni.edu or Alice Müller <alice@uni.edu>"
                fullWidth
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ whiteSpace: 'nowrap', borderColor: primaryColor, color: darkColor, alignSelf: 'flex-start', mt: '2px' }}
                onClick={() => addEmailInvitee()}
              >
                Add
              </Button>
            </Box>
            {emailInvitees.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {emailInvitees.map(({ email, name }) => (
                  <Chip
                    key={email}
                    label={name ? `${name} <${email}>` : email}
                    size="small"
                    onDelete={() => setEmailInvitees((prev) => prev.filter((e) => e.email !== email))}
                    sx={{ bgcolor: lightColor }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Message field */}
        <TextField
          label={getMessageLabel()}
          multiline
          rows={3}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={getMessagePlaceholder()}
          disabled={checkingInvitations}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: darkColor,
            },
          }}
        />



{/* {entityType === 'consortium' && (
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2,
            bgcolor: 'info.50',
            border: '1px solid',
            borderColor: 'info.100'
          }}
          icon={isSuggestMode ? <LightbulbIcon /> : <GroupIcon />}
        >
          {isSuggestMode 
            ? 'Users who are already members, invited, or have received suggestions for this entity are filtered out.' 
            : 'Users who are already members or have pending invitations are filtered out.'}
        </Alert>
      )} */}

        {/* Inline errors (validation only — success/partial go to global snackbar) */}
        {error && !success && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <SentInvitationsPanel open={open} mode={mode} entityType={entityType} entityId={entity?.id} accent={primaryColor} />
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        pb: 2,
        borderTop: `1px solid ${isSuggestMode ? '#ad89ad22' : 'divider'}`,
        pt: 2
      }}>
        <Button 
          onClick={handleClose} 
          disabled={loading || checkingInvitations}
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleAction} 
          variant="contained"
          disabled={!isActionEnabled() || loading || checkingInvitations}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            bgcolor: primaryColor,
            '&:hover': {
              bgcolor: darkColor,
            },
            '&.Mui-disabled': {
              bgcolor: isSuggestMode ? '#ad89ad22' : 'action.disabledBackground',
              color: isSuggestMode ? '#ad89ada0' : 'action.disabled'
            }
          }}
        >
          {loading ? 'Sending...' : checkingInvitations ? 'Checking...' : getActionButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InviteToSuggestEntityDialog;